import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../i18n';
import { useAuth } from '../context/AuthContext';
import { PLAY_STORE_URL } from '../lib/usePwaInstall';

const PRIMARY_BUTTON = 'bg-primary text-primary-foreground py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors';
const SECONDARY_BUTTON = 'text-primary py-3 rounded-lg font-semibold border border-border hover:bg-muted transition-colors';

// Opens the app's own screen for the link (lingo:// scheme), for when Android shows
// this page instead of handing the App Link to the installed app (context/Mobile.md).
function appIntentUrl(token, newAccount) {
  const path = `login/verify?token=${encodeURIComponent(token)}${newAccount ? '&new=1' : ''}`;
  return `intent://${path}#Intent;scheme=lingo;package=io.laftel.lingo;S.browser_fallback_url=${encodeURIComponent(PLAY_STORE_URL)};end`;
}

// Landing page for the sign-in link in the code email (and the Android App Link
// when the app isn't installed or Android doesn't hand the link to it). The token is only sent after a click: email
// scanners that open links, even running their scripts, would otherwise spend it.
function LoginVerifyPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { verifyCode } = useAuth();
  const [token] = useState(searchParams.get('token') || '');
  const [newAccount] = useState(searchParams.get('new') === '1');
  const [fromApp] = useState(searchParams.get('app') === '1');
  const [showApp] = useState(() => Boolean(token) && /Android/i.test(navigator.userAgent));
  const appFirst = showApp && fromApp;
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    navigate('/login/verify', { replace: true });
  }, [navigate]);

  const signInWithLink = async () => {
    setError(null);
    setLoading(true);
    const result = await verifyCode({ token });
    setLoading(false);
    if (result.success) {
      navigate(result.isNewUser ? '/language-setup' : '/dashboard');
    } else {
      setError(result.error || t('auth.code.failed'));
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-card rounded-lg shadow-xl p-6 sm:p-8 border border-border">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            {newAccount ? t('auth.code.createTitle') : t('auth.code.signInTitle')}
          </h1>
          <p className="text-muted-foreground">
            {newAccount ? t('auth.link.createText') : t('auth.link.signInText')}
          </p>
        </div>

        <div className="space-y-6">
          {error && (
            <div className="bg-destructive/20 border border-destructive text-destructive-foreground px-4 py-3 rounded">
              {error}
            </div>
          )}
          {error ? (
            <Link to="/login" className="block text-center text-primary hover:underline">
              {t('auth.link.backToSignIn')}
            </Link>
          ) : (
            <>
              {appFirst && (
                <a href={appIntentUrl(token, newAccount)} className={`block text-center ${PRIMARY_BUTTON}`}>
                  {t('auth.link.openApp')}
                </a>
              )}
              <button
                type="button"
                onClick={signInWithLink}
                disabled={loading || !token}
                className={`w-full disabled:opacity-50 disabled:cursor-not-allowed ${appFirst ? SECONDARY_BUTTON : PRIMARY_BUTTON}`}
              >
                {loading
                  ? t('auth.code.checking')
                  : appFirst
                    ? t('auth.link.useBrowser')
                    : newAccount ? t('auth.code.createAccount') : t('auth.code.signIn')}
              </button>
              {showApp && !fromApp && (
                <a href={appIntentUrl(token, newAccount)} className="block text-center text-primary hover:underline">
                  {t('auth.link.openApp')}
                </a>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default LoginVerifyPage;
